import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface Agent {
  id: number
  username: string
}

interface Webhook {
  id: number
  name: string
  description: string | null
  responsible_agent_id: number
  active: boolean
  token: string
  inbound_url: string
}

interface Props {
  webhook: Webhook
  agents: Agent[]
}

export default function Edit({ webhook, agents }: Props) {
  const [copied, setCopied] = useState(false)

  const { data, setData, put, processing, errors } = useForm({
    webhook: {
      name: webhook.name,
      description: webhook.description || '',
      responsible_agent_id: webhook.responsible_agent_id,
      active: webhook.active,
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/webhooks/webhooks/${webhook.id}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete webhook '${webhook.name}'? This will also delete all associated events.`)) {
      router.delete(`/manage/webhooks/webhooks/${webhook.id}`)
    }
  }

  function handleCopyUrl() {
    navigator.clipboard.writeText(webhook.inbound_url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <SidebarLayout section="manage" title="Edit webhook">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Inbound URL
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                backgroundColor: 'action.hover',
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                flexGrow: 1,
                wordBreak: 'break-all',
              }}
            >
              {webhook.inbound_url}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
              <IconButton size="small" onClick={handleCopyUrl}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.webhook.name}
            onChange={e => setData('webhook', { ...data.webhook, name: e.target.value })}
            error={'webhook.name' in errors}
            helperText={(errors as Record<string, string>)['webhook.name']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={data.webhook.description}
            onChange={e => setData('webhook', { ...data.webhook, description: e.target.value })}
            error={'webhook.description' in errors}
            helperText={(errors as Record<string, string>)['webhook.description']}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'webhook.responsible_agent_id' in errors}>
            <InputLabel>Responsible agent</InputLabel>
            <Select
              value={data.webhook.responsible_agent_id}
              label="Responsible agent"
              onChange={e => setData('webhook', { ...data.webhook, responsible_agent_id: e.target.value as number })}
            >
              {agents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>{agent.username}</MenuItem>
              ))}
            </Select>
            {'webhook.responsible_agent_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['webhook.responsible_agent_id']}</FormHelperText>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.webhook.active}
                onChange={e => setData('webhook', { ...data.webhook, active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mb: 2.5 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update webhook
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/webhooks/webhooks" style={{ color: '#1a1a1a' }}>Back to webhooks</Link>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h3" color="error" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deleting this webhook will permanently remove it and all associated events.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
          >
            Delete webhook
          </Button>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
